import { InputStepper } from '@/components';
import { Column, Element, Icon, IconButton, Row } from '@vuddy/components';
import Image from 'next/image';
import React, { useCallback } from 'react';
import * as styles from '../common/bottom.css';
import type { ProductItemWithAdditionalInfo } from '../common/useProductItemList';
import { SelectedProductOption } from './types';

interface Props {
  item: SelectedProductOption;
  product: ProductItemWithAdditionalInfo;
  onDelete: (item: SelectedProductOption) => void;
  onQuantityChange: (item: SelectedProductOption, quantity: number) => void;
  hasVerificationCode?: boolean;
  verificationCodeName?: string;
  onCodeInputClick?: (productId: number) => void;
}

export const CartItem = React.memo(
  ({
    item,
    product,
    onQuantityChange,
    onDelete,
    hasVerificationCode,
    verificationCodeName,
    onCodeInputClick,
  }: Props) => {
    const isPurchaseLimited =
      !product.isPurchasable && product.remainingPurchaseCount === 0;
    const disabled = product.saleStatus !== 'ON_SALE' || isPurchaseLimited;

    const handleQuantityChange = useCallback(
      (newQuantity: number) => {
        onQuantityChange(item, newQuantity);
      },
      [onQuantityChange, item]
    );

    const handleDelete = useCallback(() => {
      onDelete(item);
    }, [onDelete, item]);

    const handleCodeInputClick = useCallback(() => {
      onCodeInputClick?.(item.productId);
    }, [onCodeInputClick, item.productId]);

    // 옵션명 조합 문자열 만들기
    const optionSummary = item.optionCategories
      ?.map(cat => {
        const catData = product.optionCategories?.find(
          c => c.id === cat.optionCategoryId
        );
        if (!catData) return '';

        // 선택안함 옵션이 선택된 경우
        if (cat.optionMenuIds.includes(-1)) {
          return `[옵션-${catData.name}]: 선택안함`;
        }

        const optionNames = catData.options
          .filter(opt => cat.optionMenuIds.includes(opt.id))
          .map(opt => opt.name)
          .join(', ');

        return `[옵션-${catData.name}]: ${optionNames}`;
      })
      .filter(Boolean)
      .join(' / ');

    return (
      <Column
        key={item.productId}
        gap="gap_xl"
        backgroundColor="tertiary"
        borderRadius="radius_large"
        paddingX="padding_small"
        paddingY="padding_xs">
        <Row
          justifyContent="space-between"
          alignItems="flex-start"
          gap="gap_base">
          <Column flex={1}>
            <Element typography="bodyXsAccent" fontWeight="bold">
              {product.name}
            </Element>
            <Element typography="bodyXs" color="secondary">
              {product.description}
              {optionSummary ? ` | ${optionSummary}` : ''}
            </Element>
            {hasVerificationCode && (
              <Row
                as="button"
                alignItems="center"
                gap="gap_2xs"
                marginTop="gap_base"
                onClick={handleCodeInputClick}
                className={styles.verificationCodeButtonPhysical}>
                <Icon
                  name="FILL_PROMOTION_CODE"
                  size="16px"
                  color="secondary"
                />
                <Element
                  typography="captionBaseAccent"
                  color="secondary"
                  className={
                    verificationCodeName
                      ? styles.verificationCodeText
                      : styles.verificationCodeTextUnderline
                  }>
                  {verificationCodeName || '코드 입력'}
                </Element>
              </Row>
            )}
          </Column>
          <IconButton
            icon={{
              name: 'OUTLINE_CLOSE',
              size: '20px',
              color: 'tertiary',
            }}
            onClick={handleDelete}
          />
        </Row>
        <Row justifyContent="space-between" alignItems="center" gap="gap_base">
          <Element style={{ width: '90px' }}>
            <InputStepper
              value={item.quantity ?? 1}
              onChange={handleQuantityChange}
              min={1}
              max={product.allowedMaxOrderCount ?? product.remainQuantity}
              disabled={disabled}
              tabIndex={-1}
            />
          </Element>
          <Column alignItems="flex-end" gap="gap_2xs">
            <Element typography="bodyBaseAccent" fontWeight="bold">
              {item.totalPrice?.toLocaleString() ?? 0}원
            </Element>
            {product.pointBenefit && (
              <Row gap="gap_2xs" alignItems="center">
                {product.pointBenefit.type === 'SERIES_POINT' ? (
                  <Icon name="ETC_POINT" size="16px" />
                ) : (
                  <Image
                    width={16}
                    height={16}
                    alt="포인트 icon"
                    src="/images/icons/inish-ic-point.png"
                  />
                )}
                <Element typography="captionBaseAccent" color="vuddyPrimary">
                  {product.pointBenefit.text}
                </Element>
              </Row>
            )}
          </Column>
        </Row>
      </Column>
    );
  }
);

CartItem.displayName = 'CartItem';
